export const guides = [
  {
    slug: 'system-design-basics',
    title: 'System Design Basics',
    description: 'Master the foundational principles: scalability, reliability, availability, CAP theorem, and the core trade-offs every engineer must understand.',
    emoji: '🏗️',
    thumbnail: '/thumbnails/basics.webp',
    tags: ['Foundations', 'Scalability', 'CAP Theorem'],
    readTime: '25 min',
    difficulty: 'Beginner',
    color: 'from-blue-500 to-cyan-600',
    contentFile: '/content/system-design-basics.md',
    chapters: ['Design Goals', 'Scalability', 'Databases', 'Caching', 'Networking', 'Communication'],
  },
  {
    slug: 'design-instagram-newsfeed',
    title: 'Design Instagram News Feed',
    description: 'Build a feed generation system for 500M+ users. Dive into fan-out strategies, real-time delivery, and content personalization at scale.',
    emoji: '📱',
    thumbnail: '/thumbnails/instagram.webp',
    tags: ['Social Media', 'Feed Generation', 'Caching'],
    readTime: '20 min',
    difficulty: 'Intermediate',
    color: 'from-pink-500 to-rose-600',
    contentFile: '/content/design-instagram-newsfeed.md',
    chapters: ['Requirements', 'API Design', 'Feed Generation', 'Caching Strategy'],
  },
  {
    slug: 'design-youtube',
    title: 'Design YouTube',
    description: 'Architect a video platform serving billions of hours of content daily. Video upload pipelines, CDN, adaptive streaming, and recommendation systems.',
    emoji: '🎥',
    thumbnail: '/thumbnails/youtube.webp',
    tags: ['Video Streaming', 'CDN', 'Storage'],
    readTime: '22 min',
    difficulty: 'Intermediate',
    color: 'from-red-500 to-orange-600',
    contentFile: '/content/design-youtube.md',
    chapters: ['Upload Pipeline', 'Video Processing', 'Streaming', 'Recommendations'],
  },
  {
    slug: 'design-tinyurl',
    title: 'Design TinyURL',
    description: 'Build a URL shortening service handling millions of redirections per second. ID generation, hashing algorithms, and analytics at scale.',
    emoji: '🔗',
    thumbnail: '/thumbnails/tinyurl.webp',
    tags: ['URL Shortening', 'Hashing', 'Distributed ID'],
    readTime: '15 min',
    difficulty: 'Beginner',
    color: 'from-violet-500 to-purple-600',
    contentFile: '/content/design-tinyurl.md',
    chapters: ['Requirements', 'ID Generation', 'Redirection', 'Analytics'],
  },
  {
    slug: 'design-rate-limiter',
    title: 'Design a Rate Limiter',
    description: 'Protect your APIs from abuse with distributed rate limiting. Token bucket, leaky bucket, sliding window algorithms and distributed implementation.',
    emoji: '🚦',
    thumbnail: '/thumbnails/rate-limiter.webp',
    tags: ['API Gateway', 'Rate Limiting', 'Distributed Systems'],
    readTime: '18 min',
    difficulty: 'Intermediate',
    color: 'from-amber-500 to-yellow-600',
    contentFile: '/content/design-rate-limiter.md',
    chapters: ['Algorithms', 'Distributed Design', 'Edge Cases', 'API Integration'],
  },
  {
    slug: 'design-whatsapp',
    title: 'Design WhatsApp',
    description: 'Build a real-time messaging system for 2 billion users. WebSockets, message queues, end-to-end encryption, and delivery guarantees.',
    emoji: '💬',
    thumbnail: '/thumbnails/whatsapp.webp',
    tags: ['Real-time', 'Messaging', 'WebSockets'],
    readTime: '20 min',
    difficulty: 'Intermediate',
    color: 'from-green-500 to-emerald-600',
    contentFile: '/content/design-whatsapp.md',
    chapters: ['Real-time Messaging', 'Encryption', 'Delivery Guarantees', 'Presence'],
  },
  {
    slug: 'design-search-system',
    title: 'Design a Search System',
    description: 'Architect a search engine with inverted indexes, relevance ranking, query parsing, and sub-second results for billions of documents.',
    emoji: '🔍',
    thumbnail: '/thumbnails/search.webp',
    tags: ['Search', 'Inverted Index', 'Ranking'],
    readTime: '18 min',
    difficulty: 'Advanced',
    color: 'from-blue-600 to-indigo-700',
    contentFile: '/content/design-search-system.md',
    chapters: ['Inverted Index', 'Relevance Ranking', 'Query Processing', 'Caching'],
  },
  {
    slug: 'design-airbnb',
    title: 'Design Airbnb',
    description: 'Build a marketplace platform with search, booking, payments, and recommendation systems. Handle double-booking prevention and inventory management.',
    emoji: '🏠',
    thumbnail: '/thumbnails/airbnb.webp',
    tags: ['Marketplace', 'Booking System', 'Geo-search'],
    readTime: '22 min',
    difficulty: 'Advanced',
    color: 'from-rose-500 to-pink-600',
    contentFile: '/content/design-airbnb.md',
    chapters: ['Search & Discovery', 'Booking Flow', 'Payment Processing', 'Recommendations'],
  },
  {
    slug: 'design-notification-system',
    title: 'Design a Notification System',
    description: 'Build a multi-channel notification platform (push, email, SMS) with prioritization, batching, delivery tracking, and user preference management.',
    emoji: '🔔',
    thumbnail: '/thumbnails/notification.webp',
    tags: ['Push Notifications', 'Multi-channel', 'Event-driven'],
    readTime: '16 min',
    difficulty: 'Intermediate',
    color: 'from-orange-500 to-amber-600',
    contentFile: '/content/design-notification-system.md',
    chapters: ['Multi-channel Delivery', 'Prioritization', 'Delivery Tracking', 'User Preferences'],
  },
  {
    slug: 'design-distributed-logging-system',
    title: 'Design a Distributed Logging System',
    description: 'Build an ELK-style logging infrastructure for microservices. Log aggregation, real-time processing, storage, alerting, and dashboards.',
    emoji: '🪵',
    tags: ['Logging', 'Observability', 'Stream Processing'],
    readTime: '16 min',
    difficulty: 'Advanced',
    color: 'from-teal-500 to-cyan-600',
    contentFile: '/content/design-distributed-logging-system.md',
    chapters: ['Log Collection', 'Stream Processing', 'Storage', 'Alerting'],
  },
  {
    slug: 'design-cab-sharing-system',
    title: 'Design a Cab Sharing System',
    description: 'Architect an Uber/Ola-style platform with real-time driver matching, location tracking, fare calculation, and surge pricing.',
    emoji: '🚖',
    tags: ['Location Services', 'Real-time Matching', 'Geo-indexing'],
    readTime: '24 min',
    difficulty: 'Advanced',
    color: 'from-yellow-500 to-orange-600',
    contentFile: '/content/design-cab-sharing-system.md',
    chapters: ['Driver Matching', 'Location Tracking', 'Fare Calculation', 'Surge Pricing'],
  },
  {
    slug: 'design-google-maps',
    title: 'Design Google Maps',
    description: 'Architect a global navigation system serving 1B users. Location search, route calculation, real-time traffic, ETA prediction, and map tile rendering at massive scale.',
    emoji: '🗺️',
    thumbnail: '/thumbnails/google-maps.webp',
    tags: ['Navigation', 'Geospatial', 'Real-time Traffic'],
    readTime: '35 min',
    difficulty: 'Advanced',
    color: 'from-green-600 to-teal-700',
    contentFile: '/content/design-google-maps.md',
    chapters: ['Requirements', 'Capacity', 'API Design', 'HLD', 'Geospatial', 'Routing'],
  },
  {
    slug: 'design-otp-service',
    title: 'Design an OTP Service',
    description: 'Build a secure, high-availability OTP generation and validation service. Rate limiting, security hardening, multi-region failover, and delivery.',
    emoji: '🔐',
    tags: ['Security', 'Authentication', 'High Availability'],
    readTime: '15 min',
    difficulty: 'Intermediate',
    color: 'from-indigo-500 to-violet-600',
    contentFile: '/content/design-otp-service.md',
    chapters: ['OTP Generation', 'Validation Flow', 'Security', 'Multi-region'],
  },
  {
    slug: 'design-stock-broker-service',
    title: 'Design a Stock Broker Service',
    description: 'Build a Zerodha/Robinhood-style trading platform with real-time price feeds (SSE), FIX protocol, order state machines, and financial consistency.',
    emoji: '📈',
    tags: ['FinTech', 'Real-time', 'FIX Protocol'],
    readTime: '28 min',
    difficulty: 'Advanced',
    color: 'from-emerald-600 to-green-700',
    contentFile: '/content/design-stock-broker-service.md',
    chapters: ['Watchlist & Streaming', 'Order Management', 'Portfolio', 'Reliability'],
  },
];

export const getDifficultyColor = (difficulty) => {
  switch (difficulty) {
    case 'Beginner': return '#4ade80';
    case 'Intermediate': return '#60a5fa';
    case 'Advanced': return '#f97316';
    default: return '#94a3b8';
  }
};
